/**
 * @file Portfolio host detection (EPD-10104).
 *
 * On the marketing-domain portfolio host (www.aspire.io/portfolio/<slug>) the
 * web worker serves this SPA with a `<meta name="aspire-app-origin">` tag
 * holding the app origin (creators.aspireiq.com). Its presence means:
 *
 * - `/portfolio/$profileSlug` renders the portfolio;
 * - API calls, `/cdn/` images and other app resources go to the app origin
 *   (the marketing domain only serves `/portfolio/<slug>`), without
 *   credentials;
 * - no service worker, version polling, session fetch or login redirect.
 *
 * On every other host the tag is absent and nothing here changes behaviour.
 */

import { APP_ORIGIN_META_NAME } from "@repo/core";

function parseOrigin(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    // The marker steers API calls and asset loads, so only trust https (plain
    // http only for local development).
    const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    return url.protocol === "https:" || (url.protocol === "http:" && local)
      ? url.origin
      : null;
  } catch {
    return null;
  }
}

/** The app origin when running on the portfolio host, else null. */
export function getPortfolioHostAppOrigin(
  doc: Document | undefined = typeof document === "undefined"
    ? undefined
    : document,
): string | null {
  const meta = doc?.querySelector(`meta[name="${APP_ORIGIN_META_NAME}"]`);
  return parseOrigin(meta?.getAttribute("content"));
}

/** True when this page was served on the marketing-domain portfolio host. */
export function isPortfolioHost(doc?: Document): boolean {
  return getPortfolioHostAppOrigin(doc) !== null;
}

/**
 * Origin to prefix root-relative app resources (`/cdn/...`) with: the app
 * origin on the portfolio host, "" (same origin) everywhere else.
 */
export function appResourceOrigin(doc?: Document): string {
  return getPortfolioHostAppOrigin(doc) ?? "";
}

/**
 * Base URL for the API: `<app origin>/api` on the portfolio host, otherwise
 * `fallback` (VITE_API_URL or the same-origin "/api").
 */
export function apiBaseUrl(fallback: string, doc?: Document): string {
  const appOrigin = getPortfolioHostAppOrigin(doc);
  return appOrigin ? `${appOrigin}/api` : fallback;
}

/**
 * Credentials for API calls: none from the portfolio host (the API allows that
 * origin without credentials, so no session crosses to the marketing domain);
 * the session cookie everywhere else.
 */
export function apiCredentials(doc?: Document): RequestCredentials {
  return isPortfolioHost(doc) ? "omit" : "include";
}
